import { Component, EventEmitter, Input, OnInit, Output, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';
import { CustomerServiceIssueType } from '../../../../../core/models/customer-service/customer-service-issue-type.model';
import { CustomerServiceIssueTypeMapper } from '../../../../../core/mappers/customer-service/mapper-customer-service-issue-type.service';
import { ApiCustomerServiceIssueTypeService } from '../../../../../core/services/api/customer-service/api-customer-service-issue-type.service';
import { MenuPermissionService } from '../../../../../core/services/menu-permission.service';
import { ModalAdminActivityHistoryComponent } from '../../system-settings/modal-admin-activity-history/modal-admin-activity-history.component';
import { AdminActivityEntityTypes } from '../../../../../core/models/system-settings/admin-activity-log.model';

@Component({
  selector: 'app-modal-customer-service-issue-type',
  standalone: true,
  imports: [CommonModule, FormsModule, ModalAdminActivityHistoryComponent],
  templateUrl: './modal-customer-service-issue-type.component.html',
  styleUrl: './modal-customer-service-issue-type.component.css'
})
export class ModalCustomerServiceIssueTypeComponent implements OnInit {
  @Input() editRow: CustomerServiceIssueType | null = null;
  @Output() close = new EventEmitter<void>();
  @Output() saveSuccess = new EventEmitter<void>();

  readonly activityEntityTypes = AdminActivityEntityTypes;
  showActivityHistory = false;

  // ================= State Variables =================
  formData!: Partial<CustomerServiceIssueType>;
  isSaving = signal(false);
  isVisible = false;

  // --- Category Dropdown Search Autocomplete ---
  categories = [
    { value: 'refund', label: 'คืนเงิน (Refund)' },
    { value: 'complaint', label: 'ข้อร้องเรียน (Complaint)' },
    { value: 'general', label: 'สอบถามทั่วไป (General)' },
    { value: 'technical', label: 'ปัญหาทางเทคนิค (Technical)' },
    { value: 'payment_sync', label: 'ปรับยอดชำระ (Payment Sync)' },
    { value: 'registration', label: 'สมัครสมาชิก/ลงทะเบียน (Registration)' }
  ];
  searchCategoryText: string = '';
  isCategoryDropdownOpen = false;

  // --- Resolution Mode Dropdown Search Autocomplete ---
  resolutionModes = [
    { value: 'refund', label: 'คืนเงิน (Refund)' },
    { value: 'payment_fix', label: 'แก้ไขการชำระเงิน/ปรับยอด (Payment Fix)' },
    { value: 'registration_fix', label: 'แก้ไขข้อมูลลงทะเบียน (Registration Fix)' },
    { value: 'inquiry', label: 'ตอบข้อซักถาม (Inquiry)' },
    { value: 'complaint', label: 'จัดการข้อร้องเรียน (Complaint)' }
  ];
  searchResolutionText: string = '';
  isResolutionDropdownOpen = false;

  private readonly api = inject(ApiCustomerServiceIssueTypeService);
  private readonly mapper = inject(CustomerServiceIssueTypeMapper);
  private readonly toast = inject(ToastService);
  private readonly cookieService = inject(CookieService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);

  get isEdit(): boolean {
    return (this.editRow?.id ?? 0) > 0;
  }

  // ================= Normal Functions =================

  ngOnInit(): void {
    setTimeout(() => {
      this.isVisible = true;
    }, 50);

    this.formData = this.editRow
      ? { ...this.editRow }
      : this.mapper.createEmptyIssueType();

    // Initialize Autocomplete Texts
    const selectedCat = this.categories.find(c => c.value === this.formData.category);
    this.searchCategoryText = selectedCat ? selectedCat.label : '';

    const selectedRes = this.resolutionModes.find(r => r.value === this.formData.resolutionMode);
    this.searchResolutionText = selectedRes ? selectedRes.label : '';
  }

  onClose(): void {
    this.isVisible = false;
    setTimeout(() => {
      this.close.emit();
    }, 200);
  }

  // --- Category Autocomplete Dropdown ---
  get filteredCategories() {
    const q = this.searchCategoryText.trim().toLowerCase();
    if (!q) return this.categories;
    return this.categories.filter(c => c.label.toLowerCase().includes(q));
  }

  toggleCategoryDropdown(): void {
    this.isCategoryDropdownOpen = !this.isCategoryDropdownOpen;
  }

  selectCategory(option: { value: string; label: string }): void {
    this.formData.category = option.value;
    this.searchCategoryText = option.label;
    this.isCategoryDropdownOpen = false;
  }

  closeCategoryDropdown(): void {
    setTimeout(() => {
      this.isCategoryDropdownOpen = false;
      // Revert if typed text is invalid
      const exactMatch = this.categories.find(
        c => c.label.toLowerCase() === this.searchCategoryText.trim().toLowerCase()
      );
      if (exactMatch) {
        this.formData.category = exactMatch.value;
        this.searchCategoryText = exactMatch.label;
      } else {
        const originalVal = this.categories.find(c => c.value === this.formData.category);
        this.searchCategoryText = originalVal ? originalVal.label : '';
      }
    }, 200);
  }

  // --- Resolution Mode Autocomplete Dropdown ---
  get filteredResolutionModes() {
    const q = this.searchResolutionText.trim().toLowerCase();
    if (!q) return this.resolutionModes;
    return this.resolutionModes.filter(r => r.label.toLowerCase().includes(q));
  }

  toggleResolutionDropdown(): void {
    this.isResolutionDropdownOpen = !this.isResolutionDropdownOpen;
  }

  selectResolution(option: { value: string; label: string }): void {
    this.formData.resolutionMode = option.value;
    this.searchResolutionText = option.label;
    this.isResolutionDropdownOpen = false;
  }

  closeResolutionDropdown(): void {
    setTimeout(() => {
      this.isResolutionDropdownOpen = false;
      // Revert if typed text is invalid
      const exactMatch = this.resolutionModes.find(
        r => r.label.toLowerCase() === this.searchResolutionText.trim().toLowerCase()
      );
      if (exactMatch) {
        this.formData.resolutionMode = exactMatch.value;
        this.searchResolutionText = exactMatch.label;
      } else {
        const originalVal = this.resolutionModes.find(r => r.value === this.formData.resolutionMode);
        this.searchResolutionText = originalVal ? originalVal.label : '';
      }
    }, 200);
  }

  // ================= API Functions =================

  onSave(): void {
    // Validate required fields
    if (!this.formData.code?.trim()) {
      this.toast.error('กรุณาระบุรหัสประเภทเรื่อง');
      return;
    }
    if (!this.formData.name?.trim()) {
      this.toast.error('กรุณาระบุชื่อประเภทเรื่อง');
      return;
    }
    if (!this.formData.category) {
      this.toast.error('กรุณาระบุหมวดหมู่');
      return;
    }
    if (!this.formData.resolutionMode) {
      this.toast.error('กรุณาระบุรูปแบบการแก้ไข');
      return;
    }

    // Code validation: enforce alphanumeric and underscore
    const codeRegex = /^[a-z0-9_]+$/;
    const cleanCode = this.formData.code.trim().toLowerCase();
    if (!codeRegex.test(cleanCode)) {
      this.toast.error('รหัสประเภทเรื่องต้องเป็นตัวพิมพ์เล็ก ตัวเลข หรือเครื่องหมาย _ เท่านั้น');
      return;
    }

    const isEdit = this.isEdit;
    const currentUrl = '/customer-service/customer-service-issue-type';

    if (isEdit && !this.permissionService.canUpdate(currentUrl)) {
      this.toast.error('ไม่มีสิทธิ์แก้ไข');
      return;
    }
    if (!isEdit && !this.permissionService.canCreate(currentUrl)) {
      this.toast.error('ไม่มีสิทธิ์สร้าง');
      return;
    }

    const userId = Number(localStorage.getItem('userId') || this.cookieService.get('user_id') || 0);
    const menuId = this.permissionService.getMenuId(currentUrl) ?? 0;

    if (!userId || !menuId) {
      this.toast.error('ไม่พบข้อมูลผู้ใช้หรือสิทธิ์เมนู');
      return;
    }

    const payload = this.mapper.mapPayload(this.formData, userId, menuId);
    const request$ = isEdit
      ? this.api.updateIssueType(this.formData.id!, payload)
      : this.api.insertIssueType(payload);

    this.isSaving.set(true);
    request$.subscribe({
      next: (res) => {
        this.isSaving.set(false);
        if (res.isSuccess) {
          this.toast.success(res.message || 'บันทึกรายการสำเร็จ');
          this.saveSuccess.emit();
          this.onClose();
        } else {
          this.toast.error(res.message || 'บันทึกไม่สำเร็จ');
        }
      },
      error: (err) => {
        this.isSaving.set(false);
        this.toast.error(err?.error?.message || 'เกิดข้อผิดพลาดในการเชื่อมต่อเซิร์ฟเวอร์');
        console.error(err);
      }
    });
  }

  get canShowActivityHistory(): boolean {
    return (this.formData.id ?? 0) > 0;
  }

  openActivityHistory(): void {
    if (!this.canShowActivityHistory) return;
    this.showActivityHistory = true;
  }

  closeActivityHistory(): void {
    this.showActivityHistory = false;
  }
}
